// @ts-strict-ignore
import React, { useContext, useState } from 'react';
import { DatePicker } from '@usealloy/component-library';
import { buildDateTimeDropdownOption, getRightAttributeValue } from './helpers';
import { selectRuleBuilderActiveSubEditor, selectRuleBuilderPanelBoxContainerWidth } from 'flow/store/redux/selectors';
import { useAppSelector, useAppDispatch } from '~/store/reduxHooks';
import type { DropdownOption } from 'flow/editor/components/NodeEditorDrawer/types';
import { updateThreshold } from 'flow/store/redux/thunks/thresholdUpdates';
import { createWorkflowHistoryThunk } from 'flow/store/redux/thunks';
import { Operation } from 'flow/service/changelog/types';
import { createUiEffectContext } from 'flow/service/history';
import { changeDescription } from 'flow/store/redux/types';
import { isPreviewAction } from 'flow/service/actions';
import type { Dayjs } from 'dayjs';
import dayjs from 'dayjs';
import { computeRuleItemLengthLimit, getMultiSelectProps } from '../helpers';
import { ThresholdContext } from '../context';
import RuleMultiSelect from '../../RuleTemplate/RuleMultiSelect';
import { RuleBuilderContext } from '../../../context';
import { UserContext } from '~/dashboard/context';
import { getIsPreview } from '../../../helpers';

interface RHSDatePickerProps {
  shouldUseDefaultString: boolean;
}

const RHSDatePicker = ({ shouldUseDefaultString }: RHSDatePickerProps) => {
  const dispatch = useAppDispatch();

  const activeSubEditor = useAppSelector(selectRuleBuilderActiveSubEditor);
  const { editMode, sourceAction } = useContext(RuleBuilderContext);
  const { featureFlags } = useContext(UserContext);
  const { lhsDataType, populatedThreshold } = useContext(ThresholdContext);

  const ruleEditorPanelBoxContainerWidth = useAppSelector(selectRuleBuilderPanelBoxContainerWidth);
  const lengthLimit = computeRuleItemLengthLimit(ruleEditorPanelBoxContainerWidth);

  const [isOpen, setIsOpen] = useState(false);

  const selectedOption = buildDateTimeDropdownOption({
    lhsDataType,
    populatedThreshold,
    lengthLimit,
  });

  const handleChange = (option: DropdownOption) => {
    const value = getRightAttributeValue({
      lhsDataType,
      shouldUseDefaultString,
      value: option.value,
    });

    const stateChange = () => dispatch(
      updateThreshold.rightValue(populatedThreshold.publicId, value),
    );

    dispatch(createWorkflowHistoryThunk(
      stateChange,
      Boolean(featureFlags?.workflowEditorUndoRedo) && !isPreviewAction(sourceAction),
      {
        operation: Operation.Modify,
        uiEffectContext: createUiEffectContext(changeDescription.threshold, {
          publicId: populatedThreshold.publicId,
          actionId: populatedThreshold.actionId,
        }),
      },
    ));
  };

  const handleDateChange = (date: Dayjs) => {
    const dateFormat = lhsDataType === 'timestamp'
      ? 'YYYY-MM-DDTHH:mm:ss[Z]'
      : 'YYYY-MM-DD';

    const formattedDate = date.format(dateFormat);
    handleChange({ label: formattedDate, value: formattedDate });
    setIsOpen(false);
  };

  const multiSelectProps = getMultiSelectProps({
    action: populatedThreshold.right.action,
    options: {
      isPreview: getIsPreview(editMode, populatedThreshold, activeSubEditor.thresholdId),
    },
  });

  return (
    <>
      {!isOpen && (
        <RuleMultiSelect
          {...multiSelectProps}
          openMenuOnFocus
          options={[selectedOption]}
          onChange={() => setIsOpen(true)}
          selectedOptions={selectedOption}
          isMulti={false}
          isNaked
        />
      )}
      {isOpen && (
        <DatePicker
          autoFocus
          open
          value={selectedOption
            ? dayjs(selectedOption.value)
            : null}
          showTime={lhsDataType === 'timestamp'}
          onChange={handleDateChange}
          onBlur={() => setIsOpen(false)}
          onKeyDown={e => {
            if (e.key === 'Escape') {
              setIsOpen(false);
            }
          }}
        />
      )}
    </>
  );
};

export default RHSDatePicker;
